import type React from "react";
import { Card, Badge, Notice, Disclosure } from "@conuti-das/prince-ui";
import { Flex, Stack } from "@/ui-prince";
import type { ValidationResult as VR } from "@/api/validatorApi";
import { FindingsList } from "./FindingsList";

interface Props {
  result: VR;
  rawEdi: string;
  HighlightComponent?: React.ComponentType<{ raw: string; result: VR | null }>;
}

export function ValidationResult({ result, rawEdi, HighlightComponent }: Props) {
  const stats = result.statistics;
  const headerSubtitle = result.valid
    ? `${stats.segments_processed} Segmente · ${stats.elements_validated} Elemente geprüft`
    : `${stats.errors} Fehler · ${stats.warnings} Warnungen · ${stats.segments_processed} Segmente`;

  return (
    <Card
      title={result.valid ? "Validierung erfolgreich" : "Validierung fehlgeschlagen"}
      header={
        <Badge tone={result.valid ? "green" : "red"} variant="solid">
          {result.valid ? "gültig" : "ungültig"}
        </Badge>
      }
    >
      <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}>
        <div style={{ fontSize: "0.85rem", color: "var(--sapContent_LabelColor)" }}>
          {headerSubtitle}
        </div>

        {result.valid ? (
          <Notice tone="positive">
            Nachricht ist gültig. {stats.segments_processed} Segmente,{" "}
            {stats.elements_validated} Elemente geprüft.
          </Notice>
        ) : (
          <Notice tone="negative">
            Nachricht ist nicht gültig.
          </Notice>
        )}

        {result.vorgaenge && result.vorgaenge.length > 0 ? (
          <>
            <Flex style={{ alignItems: "center", gap: "var(--s-3)" }}>
              <h5 style={{ margin: 0 }}>{result.vorgaenge.length} Vorgänge</h5>
              <Badge tone="green">
                {result.vorgaenge.filter((v) => v.valid).length} gültig
              </Badge>
              <Badge tone="red">
                {result.vorgaenge.filter((v) => !v.valid).length} fehlerhaft
              </Badge>
            </Flex>

            {result.vorgaenge.map((vr, i) => {
              const vid = vr.vorgang.vorgang_id ?? vr.vorgang.index + 1;
              const header = `Vorgang ${vr.vorgang.index + 1} · ${vr.vorgang.trigger_segment_tag}: ${vid}`;
              const summary = vr.valid ? "gültig" : `${vr.statistics.errors} Fehler`;
              return (
                <Disclosure
                  key={i}
                  title={`${header} — ${summary}`}
                  defaultExpanded={!vr.valid}
                >
                  <div style={{ padding: "var(--s-2)" }}>
                    <FindingsList
                      errors={vr.errors}
                      warnings={vr.warnings}
                      infos={vr.infos}
                    />
                  </div>
                </Disclosure>
              );
            })}
          </>
        ) : (
          <FindingsList
            errors={result.errors}
            warnings={result.warnings}
            infos={result.infos}
          />
        )}

        {/* EDIFACT-Quelle mit Inline-Fehler-Markierung — direkt sichtbar,
            kein Toggle. Mouseover auf rotem/gelbem Segment zeigt Popover
            mit Code, Message, Bedingung und Daten-Element. Unter jedem
            fehlerhaften Segment steht zusätzlich die Findings-Liste als
            Inline-Block (auch ohne Hover lesbar). */}
        {HighlightComponent && (
          <>
            <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
              <h5 style={{ margin: 0 }}>EDIFACT-Quelle</h5>
              <span style={{ color: "var(--sapContent_LabelColor)", fontSize: "0.8rem" }}>
                Fehlerhafte Segmente rot, Warnungen gelb hinterlegt — Hover zeigt Details
              </span>
            </Flex>
            <HighlightComponent raw={rawEdi} result={result} />
          </>
        )}
      </Stack>
    </Card>
  );
}
